import {Component, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UserType} from '@services/resource/user-type';
import {combineLatest, interval, Observable, Subject} from 'rxjs';
import {Cycle} from '@services/model/cycle';
import {CycleService} from '@services/cycle.service';
import {ExportJobDTO, MailjetExportService} from '@services/mailjet-export-service';
import {Contact, ContactQuery} from '@services/resource/contact-query';
import {map, startWith, switchMap, takeWhile, withLatestFrom} from 'rxjs/operators';
import {ProductStatus} from '@services/model/product';

import {JobInfo, JobStatus} from '@services/resource/job-info';
import {MatCard, MatCardTitle} from '@angular/material/card';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {CommonModule} from '@angular/common';
import {MatCheckbox} from '@angular/material/checkbox';
import {MarkantElementsModule} from '@markant/elements-angular';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import {MatInput} from '@angular/material/input';

@Component({
  selector: 'app-mailjet-export',
  templateUrl: './mailjet-export.component.html',
  styleUrls: ['./mailjet-export.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckbox,
    MarkantElementsModule,
    MatTable,
    MatHeaderCellDef,
    MatCellDef,
    MatRowDef,
    MatHeaderRowDef,
    MatInput,
    MatHeaderRow,
    MatRow,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
  ],
})
export class MailjetExportComponent implements OnInit {
  constructor(
    private readonly cycleService: CycleService,
    private readonly mailjetExportService: MailjetExportService,
  ) {}

  get displayedColumns() {
    return ['name', 'email', 'product'];
  }

  UserType = UserType;

  cycles$: Observable<Cycle[]>;

  query$: Observable<ContactQuery>;

  previewContacts$: Observable<Contact[]>;

  exportAction$: Subject<void> = new Subject();

  jobStatus$: Observable<JobInfo | null>;

  form = new UntypedFormGroup({
    usertype: new UntypedFormControl(),
    filterByApplication: new UntypedFormControl(false),
    pitch: new UntypedFormControl(),
    cycleFilter: new UntypedFormControl(),
    voting: new UntypedFormControl(false),
    statusIn: new UntypedFormControl(),
    name: new UntypedFormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]),
  });

  static toStatusList(x: undefined | string | [string]): ProductStatus[] | null {
    if (!x) {
      return null;
    }
    if (Array.isArray(x)) {
      return x as ProductStatus[];
    }
    return [x as ProductStatus];
  }

  ngOnInit() {
    this.cycles$ = this.cycleService.getAll(false);

    this.query$ = this.form.valueChanges.pipe(map(this.mkQuery));

    this.previewContacts$ = this.query$.pipe(switchMap((q) => this.mailjetExportService.previewQuery({...q, limit: 10})));

    const jobId$: Observable<ExportJobDTO> = this.exportAction$.pipe(
      withLatestFrom(this.query$),
      switchMap((x) => this.mailjetExportService.exportList(this.form.controls.name.value, x[1])),
    );

    this.jobStatus$ = combineLatest([jobId$, interval(1000)]).pipe(
      switchMap((x) => this.mailjetExportService.getStatus(x[0].jobId) as Observable<JobInfo>),
      takeWhile((x) => x.status !== JobStatus.COMPLETED && x.status !== JobStatus.ERROR, true),
      startWith(null),
    );
  }

  public checkGroup() {
    this.form.controls.cycleFilter.reset();
    this.form.controls.statusIn.reset();
    this.form.controls.voting.reset();
  }

  private mkQuery(f: any): ContactQuery {
    return {
      usertype: f.usertype,
      cycleFilter: f.cycleFilter || 'all',
      statusIn: MailjetExportComponent.toStatusList(f.statusIn),
      votingOnly: f.voting,
      limit: null,
    };
  }

  export(): void {
    this.exportAction$.next();
  }

  protected readonly JobStatus = JobStatus;
}
